import React, { useCallback, useEffect, useRef, useState } from 'react';
import CallStatusModal from '../components/Modals/CallStatusModal';

// Modern Components
import EmptyResultsState from '../metronic/components/EmptyResultsState';
import { ModernChatInterface } from '../metronic/components/ModernChatInterface';
import ModernQueryResultsList from '../metronic/components/ModernQueryResultsList';
import ModernResultsSection from '../metronic/components/ModernResultsSection';
import SearchingState from '../metronic/components/SearchingState';
import { Header } from '../metronic/layouts/Header';
import { Sidebar } from '../metronic/layouts/Sidebar';

export default function Conversation() {
  // State for chat and results
  const [isSearching, setIsSearching] = useState(false);
  const [queryResults, setQueryResults] = useState(null);
  const [evaluationResults, setEvaluationResults] = useState(null);
  const [emailSummary] = useState(null);
  const [callStatus] = useState(null);
  const [currentQuery, setCurrentQuery] = useState('');
  const [input, setInput] = useState('');
  const [currentJobId, setCurrentJobId] = useState('');
  const [currentJobTitle, setCurrentJobTitle] = useState('');
  const [callResults, setCallResults] = useState([]);
  const [showCallStatusesState, setShowCallStatusesState] = useState(false);
  const [isProcessingCall] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [selectedCallId] = useState(null);
  const [removedCandidates, setRemovedCandidates] = useState(new Set());
  const [currentStep, setCurrentStep] = useState('/prompt_upload_jd');
  const [messages, setMessages] = useState([]);
  const [cachedResults, setCachedResults] = useState({});
  const [removalStats] = useState({
    totalRemoved: 0,
    reasonCounts: {},
    removedSkills: {},
    removalHistory: []
  });

  // Modern UI State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chatPaneWidth, setChatPaneWidth] = useState(40); // Default 40% width
  const [isResizing, setIsResizing] = useState(false);

  // Handler functions
  const handleSearchStart = (query) => {
    setIsSearching(true);
    setQueryResults(null);
    setEvaluationResults(null);
    setCurrentQuery(query);
    setMessages([]);
    setRemovedCandidates(new Set());
  };

  // Handle workflow reset (when user wants to upload another job)
  const handleWorkflowReset = useCallback(() => {
    console.log('Resetting entire workflow from parent...');

    // Clear all results
    setQueryResults(null);
    setEvaluationResults(null);

    // Clear state
    setIsSearching(false);
    setCurrentQuery('');
    setCurrentJobId('');
    setCurrentJobTitle('');
    setInput('');

    // Clear candidates and tracking
    setRemovedCandidates(new Set());
    setCallResults([]);
    setShowCallStatusesState(false);

    // Reset messages
    setMessages([]);
    setCurrentStep('/prompt_upload_jd');

    console.log('Workflow reset complete - ready for new job description');
  }, []);

  const handleQuerySubmit = (queryData) => {
    const { results, jobId, jobTitle } = queryData;
    setCurrentJobId(jobId);
    setCurrentJobTitle(jobTitle);
    setQueryResults(results);
    setIsSearching(false);
    setShowCallStatusesState(false);
    setEvaluationResults(null);
    if (jobId) {
      localStorage.setItem(`queryResults_${jobId}`, JSON.stringify(results));
    }
  };

  const handleEvaluationResults = (results) => {
    // Filter out removed candidates from evaluation results
    const filteredEvaluationResults = results.evaluatedResumes.filter(
      candidate => !removedCandidates.has(candidate.id)
    );

    console.log('DEBUG - Evaluation results filtering:');
    console.log('Original evaluation results count:', results.evaluatedResumes.length);
    console.log('Removed candidates count:', removedCandidates.size);
    console.log('Filtered evaluation results count:', filteredEvaluationResults.length);
    console.log('Removed candidate IDs:', Array.from(removedCandidates));

    setEvaluationResults(filteredEvaluationResults);
    setCurrentJobId(results.jobId);
    setCurrentJobTitle(results.jobTitle);
    setIsSearching(false);
    if (results.jobId) {
      setCachedResults(prev => ({
        ...prev,
        [results.jobId]: {
          evaluatedResumes: filteredEvaluationResults,
          timestamp: new Date().getTime()
        }
      }));
    }
  };

  const handleCandidateRemove = (candidateId) => {
    setRemovedCandidates(prev => {
      const newSet = new Set(prev);
      newSet.add(candidateId);
      return newSet;
    });
    if (queryResults) {
      setQueryResults(prev => prev.filter(c => c.id !== candidateId));
    }
    if (evaluationResults) {
      setEvaluationResults(prev => prev.filter(c => c.id !== candidateId));
    }
  };

  const setShowCallStatuses = useCallback((value) => {
    setShowCallStatusesState(value);
  }, []);

  // Resize handler
  const containerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Enforce min (20%) and max (70%) constraints
      if (newWidth >= 20 && newWidth <= 70) {
        setChatPaneWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    if (isResizing) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Add other handlers as needed (call, transcript, etc.)

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Metronic Sidebar with Collapse Support */}
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      {/* Main Content Area - Adjusts margin based on sidebar state */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'ml-0' : 'ml-64'}`}>
        {/* Metronic Header */}
        <Header />

        {/* Modern Conversation Interface */}
        <div ref={containerRef} className="flex-1 flex p-6 overflow-hidden">
          {/* Chat Interface (Resizable - default 40% width) */}
          <div
            className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col transition-all"
            style={{ width: `${chatPaneWidth}%`, minWidth: '300px', maxWidth: '70%' }}
          >
        {/* Modern Chat Interface - Wrapper preserving all existing logic */}
        <ModernChatInterface
          onSearchStart={handleSearchStart}
          onQuerySubmit={handleQuerySubmit}
          onEvaluationResults={handleEvaluationResults}
          onWorkflowReset={handleWorkflowReset}
          setShowCallStatuses={setShowCallStatuses}
          onHighRatedCandidates={() => {}}
          onCallResultsUpdate={setCallResults}
          onTranscriptReceived={setTranscriptData}
          isProcessingCall={isProcessingCall}
          callResults={callResults}
          removedCandidates={Array.from(removedCandidates)}
          evaluationResults={evaluationResults}
          jobDescription={''}
          currentState={{
            jobId: currentJobId,
            jobTitle: currentJobTitle,
            query: currentQuery,
            input: input
          }}
          onStateUpdate={{
            setInput,
            setCurrentStep,
            setMessages
          }}
          // Pass real ChatInterface state and functions
          messages={messages}
          input={input}
          currentStep={currentStep}
          isProcessing={isSearching}
          isWaitingForUpload={false}
          isInitiatingCall={false}
          isLoadingTranscript={false}
          sendMessage={(message, step) => {
            // This will be handled by the hidden ChatInterface
            console.log('Send message:', message, 'Step:', step);
          }}
          handleFileUpload={(file) => {
            // This will be handled by the hidden ChatInterface
            console.log('File upload:', file);
          }}
          handleVoiceInput={() => {
            // This will be handled by the hidden ChatInterface
            console.log('Voice input');
          }}
        />
          </div>

          {/* Resize Handle */}
          <div
            className="w-1 bg-blue-300 hover:bg-blue-500 cursor-col-resize transition-colors mx-2 relative group"
            onMouseDown={handleResizeStart}
          >
            {/* Visual indicator on hover */}
            <div className="absolute inset-y-0 -left-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="h-full w-full bg-blue-500/20 rounded-full"></div>
            </div>
          </div>

          {/* Results Section (flexible width) */}
          <div className="flex-1 flex flex-col gap-6 overflow-y-auto">
            {/* Results Content */}
            {isSearching ? (
              <SearchingState
                query={currentQuery}
                jobTitle={currentJobTitle}
              />
            ) : (
              <>
                {queryResults && !showCallStatusesState && (
                  <ModernQueryResultsList
                    results={queryResults}
                    onCandidateRemove={handleCandidateRemove}
                    removedCandidates={removedCandidates}
                  />
                )}
                {evaluationResults && (
                  <ModernResultsSection
                    jobId={currentJobId}
                    jobTitle={currentJobTitle}
                    results={evaluationResults}
                    emailSummary={emailSummary}
                    onCallInitiate={() => {}}
                    showCallStatuses={showCallStatusesState}
                    callResults={callResults}
                    onCallResultsUpdate={setCallResults}
                    getTranscript={() => {}}
                    onCandidateRemove={handleCandidateRemove}
                    removedCandidates={removedCandidates}
                    removalStats={removalStats}
                    setMessages={setMessages}
                    setCurrentStep={setCurrentStep}
                  />
                )}
                {!isSearching && !queryResults && !evaluationResults && (
                  <EmptyResultsState />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Call status modal if needed */}
      {callStatus && !isProcessingCall && <CallStatusModal status={callStatus} />}
    </div>
  );
}
